import { Grid, Typography } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import { HandleErrorAndLoading } from "@common/handleErrorAndLoading/HandleErrorAndLoading";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NUMBER_OFFER_MAX, ROUTES } from "@constants";
import { useAddOffer } from "@hooks/useAddOffer";
import { useGetCart } from "@hooks/useGetCart";
import { getOrderAuthorization, OrderAuthorizationEnum } from "@utils/getOrderAuthorization";
import "@features/cart/css/cartPage.scss";
import { useDeleteOffer } from "@features/cart/hook/useDeleteOffer";
import { useDeletePortin } from "@features/cart/hook/useDeletePortin";
import { useAddPortin } from "@features/cart/hook/useAddPortin";
import { ICart, IOffer } from "@features/cart/ICart";
import { ProductLine } from "@features/cart/ProductLine";
import { CartTotal } from "@features/cart/CartTotal";
import { CartOfferList } from "@features/cart/CartOfferList";
import { useHistory } from "react-router-dom";
import { fixUrlLanguage } from "@utils/changeLanguage";
import { IPortinForm } from "@common/portIn/IPortinForm";

export const CartPage = () => {
    const [isOfferListDisplayed, setIsOfferListDisplayed] = useState(false);
    const [offerIdOfportInOpen, setOfferIdOfportInOpen] = useState<number | undefined>();
    const { addOfferState, addOffer } = useAddOffer();
    const { deleteOfferState, callDeleteOffer } = useDeleteOffer();
    const { deletePortinState, deletePortin } = useDeletePortin();
    const { addPortinState, addPortin } = useAddPortin();
    const [orderAuthorization, setOrderAuthorization] = useState<OrderAuthorizationEnum | undefined>(undefined);
    const { push } = useHistory();

    const checkCart = (cart?: ICart) => {
        const hasCartOffers = cart?.offers?.length;

        if (!hasCartOffers) {
            push(fixUrlLanguage(ROUTES.HOME_PAGE_URL_EN));
        }
    };
    const { userCart, setUserCart, userCartState, getCart } = useGetCart(checkCart);

    useEffect(() => {
        if (userCart && userCart.nbrAdditionalAllowedSubscriptions < 0) {
            setOrderAuthorization(getOrderAuthorization(userCart));
        } else {
            setOrderAuthorization(OrderAuthorizationEnum.OrderOk);
        }
    }, [userCart]);

    const displayAddOfferButton = !isOfferListDisplayed && userCart && userCart.nbrAdditionalAllowedSubscriptions > 0;

    const { t } = useTranslation(["cart", "common"]);

    const deleteOffer = (cartOfferId: number) => {
        callDeleteOffer(cartOfferId).then((newCart) => {
            if (newCart) {
                checkCart(newCart);
                setUserCart(newCart);
                setOfferIdOfportInOpen(undefined);
            }
        });
    };

    const addOfferCart = (catalogOfferCode: string, catalogTariffPlanCode: string, portIn: boolean) => {
        addOffer(catalogOfferCode, catalogTariffPlanCode, portIn).then((cartResult) => {
            if (cartResult) {
                setUserCart(cartResult);
                setOfferIdOfportInOpen(undefined);
            }
        });
    };

    const addPortinToCart = (cartOfferId: number, portinForm: IPortinForm, closePortInForm?: () => void) => {
        addPortin(cartOfferId, portinForm).then((cartResult) => {
            if (cartResult) {
                setUserCart(cartResult);
                setOfferIdOfportInOpen(undefined);
                if (closePortInForm) {
                    closePortInForm();
                }
            }
        });
    };

    const deletePortinCart = (cartOfferId: number) => {
        deletePortin(cartOfferId).then((cartResult) => {
            if (cartResult) {
                setUserCart(cartResult);
                setOfferIdOfportInOpen(undefined);
            }
        });
    };

    const retry = userCartState.error ? getCart : () => window.location.reload();
    const isLoading =
        userCartState.isLoading ||
        addOfferState.isLoading ||
        deleteOfferState.isLoading ||
        deletePortinState.isLoading ||
        addPortinState.isLoading;

    const error =
        userCartState.error ??
        addOfferState.error ??
        deleteOfferState.error ??
        deletePortinState.error ??
        addPortinState.error;

    return (
        <div className="cart xtest_cart_page container-boxed">
            <HandleErrorAndLoading error={error} isLoading={isLoading} retryFetch={retry}>
                {userCart && (
                    <Fragment>
                        <Grid className="xtest_cart" container direction="column">
                            <Grid container spacing={2}>
                                {userCart.offers.map((userOffer) => (
                                    <Grid item key={userOffer.offerId} xs={12}>
                                        <ProductLine
                                            addPortin={addPortinToCart}
                                            deleteOffer={deleteOffer}
                                            deletePortin={deletePortinCart}
                                            isPortOpen={offerIdOfportInOpen === userOffer.offerId}
                                            setIsPortOpen={(isPortOpen: boolean) =>
                                                isPortOpen
                                                    ? setOfferIdOfportInOpen(userOffer.offerId)
                                                    : setOfferIdOfportInOpen(undefined)
                                            }
                                            userOffer={userOffer}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                            <Grid item>
                                <CartTotal
                                    monthlyAmount={userCart.recurringChargeAmount}
                                    oneOffChargeAmount={userCart.upFrontChargeAmount}
                                />
                            </Grid>
                        </Grid>
                        {isOfferListDisplayed && (
                            <div className="offer-carousel">
                                <CartOfferList
                                    updateUserCart={(offerUpdated: IOffer) => {
                                        setIsOfferListDisplayed(false);
                                        addOfferCart(
                                            offerUpdated.catalogOfferCode,
                                            offerUpdated.catalogTariffPlanCode,
                                            false
                                        );
                                    }}
                                />
                            </div>
                        )}
                        <Grid
                            className="cart-action"
                            container
                            justify={!displayAddOfferButton ? "flex-end" : undefined}
                            spacing={2}
                        >
                            {displayAddOfferButton && (
                                <Grid item md={7} xs={8}>
                                    <Grid
                                        alignItems="center"
                                        container
                                        justify="flex-end"
                                        spacing={2}
                                        wrap="wrap-reverse"
                                    >
                                        <Grid item>
                                            <Typography align="center" color="secondary" variant="body1">
                                                {t("cart:maximumSimNumber", { max: NUMBER_OFFER_MAX })}
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <Button
                                                aria-label="AddOffer"
                                                color="primary"
                                                fullWidth
                                                id="xtest_addOffer"
                                                size="large"
                                                variant="outlined"
                                                onClick={() => setIsOfferListDisplayed(true)}
                                            >
                                                {t("cart:addSim")}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                            {orderAuthorization !== OrderAuthorizationEnum.OrderOk && (
                                <Grid container justify="flex-end">
                                    <Typography color="primary" gutterBottom variant="h4">
                                        {t("cart:needToDeleteOffer", {
                                            numberOfferToDelete: userCart.nbrAdditionalAllowedSubscriptions * -1,
                                        })}
                                    </Typography>
                                </Grid>
                            )}
                            <Grid item md={2} sm={3} xs={4}>
                                <Button
                                    color="primary"
                                    disabled={
                                        Boolean(offerIdOfportInOpen) ||
                                        orderAuthorization !== OrderAuthorizationEnum.OrderOk
                                    }
                                    fullWidth
                                    size="large"
                                    variant="contained"
                                    onClick={() => {
                                        push({ pathname: fixUrlLanguage(ROUTES.DETAILS_PAGE_URL_EN) });
                                    }}
                                >
                                    {t("common:continue")}
                                </Button>
                            </Grid>
                        </Grid>
                    </Fragment>
                )}
            </HandleErrorAndLoading>
        </div>
    );
};
